import React from "react";
import { useParams, Link } from "@/i18n/router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, ArrowLeft, ArrowRight, Calendar, Clock, User, Hash } from "lucide-react";
import { Seo } from "@/lib/seo";
import { Reveal } from "@/components/motion/Reveal";
import { PlatformCta } from "@/components/sections/PlatformCta";
import { useLang } from "@/i18n/LanguageContext";
import { api, SITE_URL, formatApiError } from "@/lib/api";

const fmt = (iso, lang) => (iso ? new Date(iso).toLocaleDateString(lang === "es" ? "es-ES" : "en-GB", { year: "numeric", month: "long", day: "numeric" }) : "");

export default function PublicationDetail() {
  const { slug } = useParams();
  const { lang, tr } = useLang();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["publication", slug],
    queryFn: async () => (await api.get(`/publications/${slug}`)).data,
    retry: false,
  });

  if (isLoading) return <div className="pt-40 pb-40 text-center text-silver/50">{tr("Loading…")}</div>;
  if (isError || !data?.publication)
    return (
      <div className="pt-40 pb-40 text-center">
        <p className="text-silver/60">{tr("Publication not found.")}</p>
        <Link to="/publications" className="mt-4 inline-block text-astro-light">{tr("Back to publications")}</Link>
      </div>
    );

  const p = data.publication;
  const related = data.related || [];

  const onDownload = async () => {
    try {
      const { data: res } = await api.post(`/publications/${slug}/download`);
      if (res.pdf_url) window.open(res.pdf_url, "_blank", "noopener");
      else toast.success(tr("Thank you — the PDF will be provided on request. Please contact us for the full document."));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ScholarlyArticle",
    headline: p.title,
    abstract: p.abstract,
    image: p.cover_image,
    datePublished: p.published_date,
    dateModified: p.updated_at,
    version: p.version,
    keywords: (p.keywords || []).join(", "),
    author: (p.authors || []).map((a) => ({ "@type": "Person", name: a.name, jobTitle: a.role })),
    publisher: { "@type": "Organization", name: "AstroLife" },
    mainEntityOfPage: `${SITE_URL}/publications/${p.slug}`,
    genre: p.pub_type,
  };

  return (
    <>
      <Seo title={p.title} description={p.seo_description || p.abstract} path={`/publications/${p.slug}`}
        image={p.cover_image} type="article" keywords={(p.keywords || []).join(", ")} jsonLd={jsonLd} locale={p.language} />

      <section className="bg-navy pt-[72px]">
        <div className="border-b border-navy-border">
          <div className="mx-auto max-w-5xl px-5 lg:px-8 py-14">
            <Link to="/publications" className="inline-flex items-center gap-1.5 text-sm text-silver/60 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" /> {tr("Publication Center")}
            </Link>
            <span className="mt-6 inline-block rounded-full bg-astro-blue/15 px-3 py-1 text-xs font-semibold text-astro-light">{tr(p.pub_type)}</span>
            <h1 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-tight text-white">{p.title}</h1>
            <p className="mt-5 max-w-3xl text-lg leading-relaxed text-silver/70">{p.abstract}</p>
          </div>
        </div>

        <div className="mx-auto max-w-5xl px-5 lg:px-8 py-14">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <Reveal>
                <div className="prose-astro max-w-none" data-testid="publication-content" dangerouslySetInnerHTML={{ __html: p.content }} />
              </Reveal>
              {related.length > 0 && (
                <div className="mt-14 border-t border-navy-border pt-8">
                  <h2 className="font-heading text-xl font-bold text-white">{tr("Related publications")}</h2>
                  <div className="mt-6 space-y-3">
                    {related.map((r) => (
                      <Link key={r.id} to={`/publications/${r.slug}`} className="group flex items-center justify-between rounded-xl border border-navy-border bg-navy-surface p-4 hover:border-astro-blue/40 transition-colors">
                        <div><span className="text-xs text-astro-light">{tr(r.pub_type)}</span><p className="font-medium text-white group-hover:text-astro-light transition-colors">{r.title}</p></div>
                        <ArrowRight className="h-4 w-4 text-silver/40" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Citation metadata sidebar */}
            <aside className="lg:col-span-4">
              <div className="sticky top-24 rounded-2xl border border-navy-border bg-navy-surface p-6" data-testid="citation-panel">
                <button onClick={onDownload} data-testid="publication-download" className="mb-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-astro-blue px-6 py-3.5 font-semibold text-white hover:bg-astro-light transition-colors">
                  <Download className="h-4 w-4" /> {tr("Download PDF")}
                </button>
                <dl className="space-y-3 text-sm">
                  <Meta icon={Hash} label={tr("Version")} value={p.version} />
                  <Meta icon={Hash} label={tr("DOI")} value={p.doi || tr("Pending")} />
                  <Meta icon={Calendar} label={tr("Published")} value={fmt(p.published_date, lang)} />
                  <Meta icon={Calendar} label={tr("Updated")} value={fmt(p.updated_at, lang)} />
                  <Meta icon={Clock} label={tr("Reading time")} value={p.reading_time ? `${p.reading_time} min` : "—"} />
                  <Meta icon={Download} label={tr("Downloads")} value={p.download_count ?? 0} />
                </dl>
                {(p.authors || []).length > 0 && (
                  <div className="mt-5 border-t border-navy-border pt-4">
                    <p className="text-xs uppercase tracking-wider text-silver/50">{tr("Authors")}</p>
                    {p.authors.map((a, i) => (
                      <p key={i} className="mt-2 flex items-center gap-2 text-sm text-silver/80"><User className="h-4 w-4 text-astro-light" /> {a.name}{a.role ? `, ${a.role}` : ""}</p>
                    ))}
                  </div>
                )}
                {(p.keywords || []).length > 0 && (
                  <div className="mt-5 border-t border-navy-border pt-4">
                    <p className="text-xs uppercase tracking-wider text-silver/50">{tr("Keywords")}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {p.keywords.map((k) => <span key={k} className="rounded border border-navy-border px-2 py-1 text-xs text-silver/70">{k}</span>)}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>
      <PlatformCta />
    </>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-navy-border/50 pb-2">
      <dt className="flex items-center gap-2 text-silver/50"><Icon className="h-3.5 w-3.5" /> {label}</dt>
      <dd className="text-right text-silver/90">{value}</dd>
    </div>
  );
}
